import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  AbstractControl,
  FormControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, map, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, take } from 'rxjs/operators';
import { Modal } from '@spirit-testing/component-library';

import { TenantUserService } from '../../services/tenant-user.service';
import { CustomerDetailService } from '../../services/customer-detail.service';
import { AccountService } from '../../account/account.service';
import { MAIL_NOT_DELIVERED_SNACKBAR } from '../invitation-mail-snackbar';
import {
  CustomerDTO,
  CustomerUserDTO,
  InvitationDTO,
  RoleDTO,
  TenantUserStatus,
  UpdateCustomerUserRequest,
} from '../../models/customer.model';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { warnBeforeUnload } from '../../shared/forms/warn-before-unload';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { StatusPillComponent } from '../../components/status-pill/status-pill.component';
import { SectionTitleComponent } from '../../components/section-title/section-title.component';

interface BlockingProject {
  id: string;
  name: string;
}

type TabId = 'daten' | 'rolle' | 'praeferenzen';
type UserStatus = TenantUserStatus;

interface TabDef {
  id: TabId;
  labelKey: string;
  icon: string;
}

const TABS: readonly TabDef[] = [
  { id: 'daten', labelKey: 'userEdit.tabs.stammdaten', icon: 'group' },
  { id: 'rolle', labelKey: 'userEdit.tabs.berechtigungen', icon: 'shield' },
  { id: 'praeferenzen', labelKey: 'userEdit.tabs.praeferenzen', icon: 'settings' },
];

const FIRST_LAST_NAME_MAX = 100;

/** Prüft den getrimmten Wert, weil auch das Backend vor der Prüfung trimmt: Leerzeichen am Rand sind kein Formatfehler. */
function trimmedEmailValidator(control: AbstractControl): ValidationErrors | null {
  const trimmed = new FormControl(String(control.value ?? '').trim());
  return Validators.required(trimmed) ?? Validators.email(trimmed);
}

/** Backend error codes on the user PUT that belong to the email field, mapped to its error key. */
const EMAIL_ERROR_BY_CODE: Readonly<Record<string, string>> = {
  EMAIL_ALREADY_IN_USE: 'emailInUse',
  EMAIL_DOMAIN_NOT_ALLOWED: 'domainNotAllowed',
  INVALID_EMAIL: 'email',
};

interface PendingChanges {
  name: boolean;
  status: boolean;
  role: boolean;
  /** Normalized new address, or null when the address is unchanged. */
  email: string | null;
  /** New value, or null when unchanged; an invitation's preferences never change here. */
  language: string | null;
  timezone: string | null;
}

interface PermissionItem {
  key: string;
  labelKey: string;
  default: boolean;
}

interface PermissionSection {
  id: string;
  labelKey: string;
  icon: string;
  items: readonly PermissionItem[];
}

const PERMISSION_SECTIONS: readonly PermissionSection[] = [
  {
    id: 'testcases',
    labelKey: 'userEdit.berechtigungen.sections.testcases',
    icon: 'check_circle',
    items: [
      { key: 't_view', labelKey: 'userEdit.berechtigungen.perms.t_view', default: true },
      { key: 't_edit', labelKey: 'userEdit.berechtigungen.perms.t_edit', default: true },
      { key: 't_run', labelKey: 'userEdit.berechtigungen.perms.t_run', default: true },
      { key: 't_del', labelKey: 'userEdit.berechtigungen.perms.t_del', default: false },
    ],
  },
  {
    id: 'requirements',
    labelKey: 'userEdit.berechtigungen.sections.requirements',
    icon: 'edit',
    items: [
      { key: 'a_view', labelKey: 'userEdit.berechtigungen.perms.a_view', default: true },
      { key: 'a_edit', labelKey: 'userEdit.berechtigungen.perms.a_edit', default: true },
      { key: 'a_jira', labelKey: 'userEdit.berechtigungen.perms.a_jira', default: false },
    ],
  },
];

interface LanguageOption {
  code: string;
  label: string;
}

const LANGUAGE_CODES: readonly string[] = ['de', 'en'];
/** Labels also for codes the invitation accepts beyond the offered ones, so a stored value stays readable. */
const LANGUAGE_LABELS: Readonly<Record<string, string>> = {
  de: 'Deutsch',
  en: 'English',
  fr: 'Français',
  es: 'Español',
};
const TIMEZONE_OPTIONS: readonly string[] = ['Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich', 'UTC'];
/** Column defaults of the backend, used while a user carries no preferences. */
const DEFAULT_LANGUAGE = 'de';
const DEFAULT_TIMEZONE = 'Europe/Berlin';

/** A stored value outside the offered list stays selectable, so opening and saving never changes it silently. */
function withStoredValue(options: readonly string[], stored: string): string[] {
  return options.includes(stored) ? [...options] : [...options, stored];
}

const STATUS_OPTIONS: readonly UserStatus[] = ['ACTIVE', 'PENDING', 'INACTIVE'];

@Component({
  selector: 'app-user-edit-page',
  imports: [
    TranslatePipe,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    DatePipe,
    Modal,
    AvatarComponent,
    StatusPillComponent,
    SectionTitleComponent,
  ],
  templateUrl: './user-edit-page.component.html',
  styleUrl: './user-edit-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserEditPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly tenantUserService = inject(TenantUserService);
  private readonly customerDetailService = inject(CustomerDetailService);
  private readonly accountService = inject(AccountService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);
  private readonly emailInput = viewChild<ElementRef<HTMLInputElement>>('emailInput');

  readonly user = signal<CustomerUserDTO | null>(null);
  readonly customer = signal<CustomerDTO | null>(null);
  readonly roles = signal<RoleDTO[]>([]);
  readonly currentUserRole = signal<string | null>(null);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  readonly activeTab = signal<TabId>('daten');
  readonly tabs = TABS;

  readonly showDeleteDialog = signal(false);
  readonly deleting = signal(false);
  private readonly deleted = signal(false);
  readonly showDeactivateConfirm = signal(false);
  private readonly deactivateConfirm$ = new Subject<boolean>();
  readonly blockingProjects = signal<BlockingProject[]>([]);

  readonly permissionSections = PERMISSION_SECTIONS;
  readonly languageOptions = computed<LanguageOption[]>(() =>
    withStoredValue(LANGUAGE_CODES, this.storedLanguage()).map((code) => ({
      code,
      label: LANGUAGE_LABELS[code] ?? code,
    })),
  );
  readonly timezoneOptions = computed(() => withStoredValue(TIMEZONE_OPTIONS, this.storedTimezone()));
  readonly statusOptions = STATUS_OPTIONS;

  private readonly customerId = signal('');
  private readonly userId = signal('');

  readonly displayName = computed(() => this.user()?.displayName ?? '');
  private readonly storedLanguage = computed(() => this.user()?.language ?? DEFAULT_LANGUAGE);
  private readonly storedTimezone = computed(() => this.user()?.timezone ?? DEFAULT_TIMEZONE);
  readonly email = computed(() => this.user()?.email ?? '');
  readonly isPlatformAdmin = computed(() => this.currentUserRole() === 'PLATFORM_ADMIN');
  readonly fieldLengths = { FIRST_LAST_NAME_MAX, EMAIL_MAX: FIELD_LENGTHS.EMAIL_MAX };

  // Als Validator statt setErrors(): Beim Tab-Wechsel meldet sich das Feld neu an und liefe dabei
  // erneut durch die Validatoren, ein gesetzter Fehler wäre weg. Er gilt nur für die abgelehnte Adresse.
  private rejectedEmail: { value: string; errorKey: string } | null = null;
  private readonly rejectedEmailValidator = (control: AbstractControl): ValidationErrors | null =>
    this.rejectedEmail && control.value === this.rejectedEmail.value ? { [this.rejectedEmail.errorKey]: true } : null;

  readonly form = this.fb.group({
    firstName: this.fb.control('', [Validators.required, Validators.maxLength(FIRST_LAST_NAME_MAX)]),
    lastName: this.fb.control('', [Validators.required, Validators.maxLength(FIRST_LAST_NAME_MAX)]),
    email: this.fb.control('', [
      trimmedEmailValidator,
      Validators.maxLength(FIELD_LENGTHS.EMAIL_MAX),
      this.rejectedEmailValidator,
    ]),
    roleId: this.fb.control(''),
    status: this.fb.control<UserStatus>('ACTIVE'),
    language: this.fb.control(DEFAULT_LANGUAGE),
    timezone: this.fb.control(DEFAULT_TIMEZONE),
  });

  // Nur lesbar: Rechte kommen aus der Rolle, Einzelrechte gibt es noch nicht (NEX-1696, NEX-1697).
  private readonly permissionState: Readonly<Record<string, boolean>> = this.initialPermissionState();

  private readonly statusSignal = toSignal(this.form.controls.status.valueChanges, {
    initialValue: this.form.controls.status.value,
  });

  readonly statusPillKind = computed(() => {
    const status = this.statusSignal();
    if (this.user()?.expired === true || status === 'EXPIRED') return 'abgelaufen';
    if (status === 'ACTIVE') return 'aktiv';
    if (status === 'PENDING') return 'pending';
    return 'inaktiv';
  });

  readonly invitationResent = signal(false);
  readonly invitationResending = signal(false);

  /**
   * Hinter der Seite kann eine Einladung oder ein echter Benutzer stehen. Unterschieden wird ueber
   * die Einladungs-Gueltigkeit: nur Einladungs-Zeilen tragen ein expiresAt.
   *
   * Der Status taugt dafuer nicht, weil die Status-Auswahl dieser Seite auch fuer einen echten
   * Benutzer PENDING zulaesst — der bekaeme sonst einen Einladungs-Block samt Aktion zum erneuten
   * Versenden, die serverseitig ins Leere laeuft.
   */
  readonly showInvitationInfo = computed(() => !!this.user()?.expiresAt);

  private readonly emailAllowedDomains = signal<string[]>([]);
  readonly emailAllowedDomainsLabel = computed(() => this.emailAllowedDomains().join(', '));

  readonly invitationExpired = computed(() => {
    const u = this.user();
    return u?.status === 'EXPIRED' || u?.expired === true;
  });

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges);
  }

  ngOnInit(): void {
    const customerId = this.route.snapshot.paramMap.get('customerId') ?? '';
    const userId = this.route.snapshot.paramMap.get('userId') ?? '';
    this.customerId.set(customerId);
    this.userId.set(userId);

    const requestedTab = this.route.snapshot.queryParamMap.get('tab');
    if (requestedTab === 'rolle' || requestedTab === 'praeferenzen' || requestedTab === 'daten') {
      this.activeTab.set(requestedTab);
    }

    this.accountService
      .getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) => {
          this.currentUserRole.set(profile.role);
          if (profile.role === 'PLATFORM_ADMIN') {
            this.form.controls.roleId.enable({ emitEvent: false });
          } else {
            this.form.controls.roleId.disable({ emitEvent: false });
          }
        },
        error: () => this.currentUserRole.set(null),
      });

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdr.markForCheck());

    const userRequest = this.loadUserOrInvitation(customerId, userId);

    forkJoin({
      customer: this.customerDetailService.getCustomer(customerId).pipe(catchError(() => of(null))),
      user: userRequest,
      roles: this.tenantUserService.getRoles(customerId).pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ customer, user, roles }) => {
        if (customer) this.customer.set(customer);
        this.roles.set(roles);
        if (user) {
          this.user.set(user);
          this.populateFormFromUser(user);
        } else {
          this.error.set('userEdit.loadError');
        }
        this.loading.set(false);
      },
      error: () => {
        this.error.set('userEdit.loadError');
        this.loading.set(false);
      },
    });
  }

  private populateFormFromUser(user: CustomerUserDTO): void {
    const { firstName, lastName } = this.resolveNameParts(user);
    this.form.patchValue({
      firstName,
      lastName,
      email: user.email ?? '',
      roleId: this.resolveRoleId(user),
      status: this.normalizeStatusForForm(user.status),
      language: this.storedLanguage(),
      timezone: this.storedTimezone(),
    });
    // Eine falsche Einladung wird widerrufen und neu versendet, nicht umgeschrieben (NEX-1350, NEX-1696).
    const invitationOnly = [this.form.controls.email, this.form.controls.language, this.form.controls.timezone];
    for (const control of invitationOnly) {
      if (this.showInvitationInfo()) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
    this.form.markAsPristine();
    this.cdr.markForCheck();
  }

  /** EXPIRED is a read-only invitation display state, not a value the settable Status dropdown offers. */
  private normalizeStatusForForm(status?: TenantUserStatus): 'ACTIVE' | 'PENDING' | 'INACTIVE' {
    return status === 'EXPIRED' ? 'PENDING' : (status ?? 'ACTIVE');
  }

  private resolveRoleId(user: CustomerUserDTO): string {
    const byCode = this.roles().find((r) => r.code === user.role);
    if (byCode) return byCode.id;
    return user.roleId ?? '';
  }

  private resolveNameParts(user: CustomerUserDTO): { firstName: string; lastName: string } {
    const first = (user.firstName ?? '').trim();
    const last = (user.lastName ?? '').trim();
    if (first || last) return { firstName: first, lastName: last };
    const display = (user.displayName ?? '').trim();
    if (!display) return { firstName: '', lastName: '' };
    const space = display.indexOf(' ');
    if (space === -1) return { firstName: '', lastName: display };
    return { firstName: display.substring(0, space).trim(), lastName: display.substring(space + 1).trim() };
  }

  setActiveTab(tab: TabId): void {
    this.activeTab.set(tab);
  }

  onTabKeydown(event: KeyboardEvent, index: number): void {
    const length = this.tabs.length;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.setActiveTab(this.tabs[(index + 1) % length].id);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.setActiveTab(this.tabs[(index - 1 + length) % length].id);
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.setActiveTab(this.tabs[0].id);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.setActiveTab(this.tabs[length - 1].id);
    }
  }

  navigateBack(): void {
    void this.router.navigate(['/client-management', this.customerId()]);
  }

  get canSave(): boolean {
    if (!this.form.valid || this.saving()) return false;
    return this.hasUnsavedChanges;
  }

  get hasUnsavedChanges(): boolean {
    const u = this.user();
    if (!u || this.deleted()) return false;
    const changes = this.pendingChanges(u);
    return changes.role || this.userFieldsChanged(changes);
  }

  /** Everything the user PUT carries; the role is saved through its own endpoint. */
  private userFieldsChanged(changes: PendingChanges): boolean {
    return (
      changes.name || changes.status || changes.email !== null || changes.language !== null || changes.timezone !== null
    );
  }

  confirmDeactivate(): Observable<boolean> {
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  private pendingChanges(u: CustomerUserDTO): PendingChanges {
    const { firstName, lastName, email, roleId, status, language, timezone } = this.form.getRawValue();
    const editable = !this.showInvitationInfo();
    const nextEmail = this.normalizeEmail(email);
    const emailChanged = editable && nextEmail !== this.normalizeEmail(u.email);
    return {
      name: firstName.trim() !== (u.firstName ?? '').trim() || lastName.trim() !== (u.lastName ?? '').trim(),
      status: status !== this.normalizeStatusForForm(u.status),
      role: this.isPlatformAdmin() && !!roleId && roleId !== u.roleId,
      email: emailChanged ? nextEmail : null,
      language: editable && language !== this.storedLanguage() ? language : null,
      timezone: editable && timezone !== this.storedTimezone() ? timezone : null,
    };
  }

  /** Same normalization as the backend, so a pure upper/lower-case edit is not a change. */
  private normalizeEmail(email: string | undefined): string {
    return (email ?? '').trim().toLowerCase();
  }

  onSave(): void {
    if (!this.canSave) return;
    const customerId = this.customerId();
    const userId = this.userId();
    const u = this.user();
    if (!customerId || !userId || !u) return;

    const { firstName, lastName, roleId, status } = this.form.getRawValue();
    const changes = this.pendingChanges(u);
    const userChanged = this.userFieldsChanged(changes);

    if (!changes.role && !userChanged) {
      this.form.markAsPristine();
      return;
    }

    this.saving.set(true);
    const request: UpdateCustomerUserRequest = { firstName, lastName, status };
    if (changes.email !== null) request.email = changes.email;
    if (changes.language !== null) request.language = changes.language;
    if (changes.timezone !== null) request.timezone = changes.timezone;
    const persistUser = userChanged
      ? this.tenantUserService
          .updateUser(customerId, userId, request)
          .pipe(map((updated) => this.applyServerUser(updated)))
      : of<CustomerUserDTO | null>(null);
    const persistRole = (input: CustomerUserDTO | null): Observable<CustomerUserDTO | null> =>
      changes.role
        ? this.tenantUserService
            .assignPlatformRole(customerId, userId, roleId)
            .pipe(map((updated) => this.applyServerUser(updated)))
        : of(input);

    persistUser.pipe(concatMap(persistRole)).subscribe({
      next: () => {
        this.saving.set(false);
        this.snackBar.open(this.translate.instant('userEdit.save.success'), undefined, {
          panelClass: 'snackbar-success',
        });
      },
      error: (err: HttpErrorResponse) => {
        this.saving.set(false);
        if (this.showServerEmailError(err)) return;
        let key = 'userEdit.save.errorGeneric';
        if (err.status === 403) key = 'userEdit.role.errorForbidden';
        else if (err.status === 400) key = 'userEdit.save.errorValidation';
        else if (err.status === 409) key = 'userEdit.role.errorLastAdmin';
        this.snackBar.open(this.translate.instant(key), undefined, { panelClass: 'snackbar-error' });
      },
    });
  }

  private showServerEmailError(err: HttpErrorResponse): boolean {
    const body = err.error as { code?: string; allowedDomains?: unknown } | null;
    const errorKey = body?.code ? EMAIL_ERROR_BY_CODE[body.code] : undefined;
    if (!errorKey) return false;
    if (body?.code === 'EMAIL_DOMAIN_NOT_ALLOWED') {
      this.emailAllowedDomains.set(Array.isArray(body.allowedDomains) ? body.allowedDomains.map(String) : []);
    }
    const email = this.form.controls.email;
    this.rejectedEmail = { value: email.value, errorKey };
    email.updateValueAndValidity();
    email.markAsTouched();
    this.activeTab.set('daten');
    this.cdr.markForCheck();
    afterNextRender(() => this.emailInput()?.nativeElement.focus(), { injector: this.injector });
    return true;
  }

  private applyServerUser(user: CustomerUserDTO): CustomerUserDTO {
    this.rejectedEmail = null;
    this.user.set(user);
    this.populateFormFromUser(user);
    return user;
  }

  isPermissionChecked(key: string): boolean {
    return this.permissionState[key] ?? false;
  }

  openDeleteDialog(): void {
    this.blockingProjects.set([]);
    this.showDeleteDialog.set(true);
  }

  onDeleteConfirmed(): void {
    if (this.deleting()) return;
    const customerId = this.customerId();
    const userId = this.userId();
    if (!customerId || !userId) return;

    this.deleting.set(true);
    this.tenantUserService.delete(customerId, userId).subscribe({
      next: () => {
        this.deleting.set(false);
        this.showDeleteDialog.set(false);
        this.blockingProjects.set([]);
        this.snackBar.open(this.translate.instant('userEdit.delete.success'), undefined, {
          panelClass: 'snackbar-success',
        });
        this.deleted.set(true);
        this.navigateBack();
      },
      error: (err: HttpErrorResponse) => {
        this.deleting.set(false);
        if (err.status === 409) {
          const projects = (err.error?.blockingProjects as BlockingProject[] | undefined) ?? [];
          this.blockingProjects.set(projects);
          return;
        }
        const key = this.deleteErrorKey(err.status);
        this.snackBar.open(this.translate.instant(key), undefined, { panelClass: 'snackbar-error' });
        this.showDeleteDialog.set(false);
        this.blockingProjects.set([]);
      },
    });
  }

  onDeleteCancelled(): void {
    if (this.deleting()) return;
    this.showDeleteDialog.set(false);
    this.blockingProjects.set([]);
  }

  onResendInvitation(): void {
    const customerId = this.customerId();
    const user = this.user();
    if (!customerId || !user || this.invitationResent() || this.invitationResending()) return;

    this.invitationResending.set(true);
    this.tenantUserService.resendInvitation(customerId, user.id).subscribe({
      next: (invitation) => {
        this.invitationResending.set(false);
        this.switchToReplacementInvitation(customerId, user, invitation);
        if (invitation.mailDelivered === false) {
          this.snackBar.open(
            this.translate.instant('customerDetail.actions.resendMailNotDelivered'),
            undefined,
            MAIL_NOT_DELIVERED_SNACKBAR,
          );
          return;
        }
        this.invitationResent.set(true);
      },
      error: () => {
        this.invitationResending.set(false);
        this.snackBar.open(this.translate.instant('customerDetail.actions.resendError'), undefined, {
          panelClass: 'snackbar-error',
        });
      },
    });
  }

  // Der Resend ersetzt die Einladung durch eine neue mit eigener ID; ein weiterer Resend, Speichern
  // oder Entfernen muss sich auf diese beziehen. Die URL folgt, damit ein Reload sie wieder öffnet.
  private switchToReplacementInvitation(
    customerId: string,
    previous: CustomerUserDTO,
    replacement: InvitationDTO,
  ): void {
    this.userId.set(replacement.id);
    this.user.set({
      ...previous,
      id: replacement.id,
      status: 'PENDING',
      expiresAt: replacement.expiresAt,
      expired: false,
    });
    void this.router.navigate(['/client-management', customerId, 'users', replacement.id, 'bearbeiten'], {
      replaceUrl: true,
      queryParamsHandling: 'preserve',
    });
    this.loadUserOrInvitation(customerId, replacement.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reloaded) => {
        if (reloaded) this.user.set(reloaded);
      });
  }

  // Die Einzelabfrage kennt nur echte Benutzer; Einladungen stehen nur in der Benutzerliste.
  private loadUserOrInvitation(customerId: string, userId: string): Observable<CustomerUserDTO | null> {
    return this.tenantUserService.getUser(customerId, userId).pipe(
      catchError(() =>
        this.tenantUserService.getUsers(customerId).pipe(
          map((users) => users.find((u) => u.id === userId) ?? null),
          catchError(() => of(null)),
        ),
      ),
    );
  }

  selectedRoleLabel(): string {
    const id = this.form.controls.roleId.value;
    const role = this.roles().find((r) => r.id === id);
    return role?.displayName ?? '—';
  }

  private deleteErrorKey(status: number): string {
    switch (status) {
      case 400:
        return 'userEdit.delete.errorSelf';
      case 403:
        return 'userEdit.delete.errorForbidden';
      case 404:
        return 'userEdit.delete.errorNotFound';
      default:
        return 'userEdit.delete.errorGeneric';
    }
  }

  private initialPermissionState(): Record<string, boolean> {
    const state: Record<string, boolean> = {};
    for (const section of PERMISSION_SECTIONS) {
      for (const item of section.items) {
        state[item.key] = item.default;
      }
    }
    return state;
  }
}
